/**
 * Shared CSV export helper.
 *
 * TaskManagement, CustomerManagement and NinjaManagement each had their own
 * copy of this function — identical except for one preserving `0`/`false`
 * values (`??` vs `||`) — and none of them neutralised a cell that starts
 * with `=`, `+`, `-`, `@`, a tab, or a carriage return. Quoting a cell does
 * not stop it being read as a formula: several spreadsheet apps evaluate a
 * quoted CSV field that starts with one of those characters anyway. Several
 * of the exported fields (a task's title, special instructions, a
 * customer's or ninja's name) are attacker-controlled — any signed-in user
 * can set them — so an admin exporting a list and opening it in a
 * spreadsheet was one crafted task away from running whatever formula that
 * user wrote, in the admin's own spreadsheet session.
 *
 * The standard mitigation: prefix such a cell with a single quote. Most
 * spreadsheet apps then render it as literal text instead of evaluating it,
 * and the character survives round-tripping the CSV back in as still-visible
 * text (rather than being silently dropped), so the export isn't lying about
 * what the underlying value was.
 */

const FORMULA_TRIGGER_CHARS = ['=', '+', '-', '@', '\t', '\r'];

function neutraliseFormula(value: string): string {
  if (value.length === 0) return value;
  return FORMULA_TRIGGER_CHARS.includes(value[0]) ? `'${value}` : value;
}

/** Exported for callers that build CSV rows by hand rather than from an array of objects. */
export function csvCell(value: unknown): string {
  const raw =
    typeof value === 'object' && value !== null
      ? JSON.stringify(value)
      : String(value ?? '');
  const safe = neutraliseFormula(raw);
  return `"${safe.replace(/"/g, '""')}"`;
}

/**
 * Builds CSV text from an array of flat-ish objects (headers taken from the
 * first row's keys) and triggers a browser download of it.
 *
 * Shows an error snackbar via `onEmpty` instead of downloading when `data`
 * is empty, matching the three callers' existing behaviour.
 */
export function exportToCSV(data: any[], filename: string, onEmpty?: () => void): void {
  if (!data || data.length === 0) {
    onEmpty?.();
    return;
  }

  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(','),
    ...data.map((row) => headers.map((header) => csvCell(row[header])).join(',')),
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
